// Screen 7 — Respond (Stage 4). The /p/:token magic-link landing.
//
// PUBLIC — the token in the URL is the credential; not wrapped in <Protected>.
// Mirrors the portal ProposalActionPage but pared to accept/decline (no counter,
// no embed) for the consumer channel. No GET-as-action: the accept/decline POST
// only fires on a real click, so email prefetchers (Outlook Safe Links) can't
// trip it.
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getProposalByToken, respondToProposal } from '../services/proposals';
import { supabase, auth, recallProvider } from '../lib/supabase';
import { capture } from '../lib/analytics';
import { Screen, Card, Eyebrow, Title, Button, Spinner } from '../components/ui';

const S = {
  LOADING: 'loading', NOT_FOUND: 'not_found', EXPIRED: 'expired', USED: 'used',
  READY: 'ready', ACCEPTED: 'accepted', REJECTED: 'rejected', ERROR: 'error',
  WRONG_ACCOUNT: 'wrong_account',
};

function formatSlot(slot) {
  if (!slot) return '';
  const s = new Date(slot.start_time);
  const date = s.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' });
  const from = s.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  const to = new Date(slot.end_time).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  return `${date}, ${from} – ${to}`;
}

export default function Respond() {
  const { token } = useParams();
  const [state, setState] = useState(S.LOADING);
  const [data, setData] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { document.title = 'when — respond'; }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const body = await getProposalByToken(token);
        if (cancelled) return;
        setData(body);
        if (body.token?.used) setState(S.USED);
        else setState(S.READY);
      } catch (err) {
        if (cancelled) return;
        if (err.status === 404) setState(S.NOT_FOUND);
        else if (err.status === 410) setState(S.EXPIRED);
        else setState(S.ERROR);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const respond = async (action) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const slot = data?.proposal?.slots?.[0];
      await respondToProposal({
        proposalId: data.proposal.id,
        token,
        action,
        selectedSlotId: action === 'accept' ? slot?.id : null,
      });
      if (action === 'accept') { capture('booking_confirmed'); setState(S.ACCEPTED); }
      else setState(S.REJECTED);
    } catch (err) {
      // 403 on this endpoint means the signed-in Google account isn't the
      // invited participant (JWT wins over the magic-link token server-side).
      // A not-signed-in click uses the token and never 403s on identity, so
      // this is an unambiguous wrong-account signal.
      if (err.status === 403) { capture('respond_wrong_account'); setState(S.WRONG_ACCOUNT); }
      else if (err.status === 401) setState(S.USED);
      else setState(S.ERROR);
    } finally {
      setSubmitting(false);
    }
  };

  // Sign out the current when session and re-enter the SAME provider with the
  // account chooser (prompt=select_account), returning to this same invite. The
  // wrong-account case is a different account within the provider they used, so
  // re-auth with that provider (Google or Microsoft), not a hardcoded one.
  const switchAccount = async () => {
    // Prefer the provider remembered at sign-in (AuthCallback); app_metadata is
    // the user's FIRST provider, not this session's, so it is only a fallback.
    const remembered = recallProvider();
    const { data: { session } } = await supabase.auth.getSession();
    const fromMeta = session?.user?.app_metadata?.provider === 'azure' ? 'microsoft' : 'google';
    const provider = remembered === 'microsoft' || remembered === 'google' ? remembered : fromMeta;
    try { await supabase.auth.signOut(); } catch { /* proceed to re-auth regardless */ }
    const next = encodeURIComponent(`/p/${token}`);
    await auth.signInWith(provider, {
      redirectTo: `${window.location.origin}/auth/callback?next=${next}`,
      selectAccount: true,
    });
  };

  const who = data?.originator?.name || 'Someone';
  const slot = data?.proposal?.slots?.[0];

  const shell = (children) => <Screen><Card>{children}</Card></Screen>;

  switch (state) {
    case S.LOADING:
      return <Screen><Spinner /></Screen>;
    case S.NOT_FOUND:
      return shell(<><Eyebrow>link not found</Eyebrow><Title className="mt-4 text-2xl">This link isn't valid.</Title><p className="mt-4 font-sans text-base text-ink/70">It may have been mistyped or withdrawn.</p></>);
    case S.EXPIRED:
      return shell(<><Eyebrow>expired</Eyebrow><Title className="mt-4 text-2xl">This proposal has expired.</Title><p className="mt-4 font-sans text-base text-ink/70">Ask {who} to send a new time.</p></>);
    case S.USED:
      return shell(<><Eyebrow>already answered</Eyebrow><Title className="mt-4 text-2xl">This one's already been answered.</Title><p className="mt-4 font-sans text-base text-ink/70">Nothing more to do here.</p></>);
    case S.ACCEPTED:
      return shell(<><Eyebrow>confirmed</Eyebrow><Title className="mt-4 text-2xl">You're on.</Title><p className="mt-4 font-sans text-base text-ink/70">{formatSlot(slot)} is now in both your calendars.</p></>);
    case S.REJECTED:
      return shell(<><Eyebrow>declined</Eyebrow><Title className="mt-4 text-2xl">Declined.</Title><p className="mt-4 font-sans text-base text-ink/70">We've let {who} know. No time was booked.</p></>);
    case S.WRONG_ACCOUNT:
      return shell(
        <>
          <Eyebrow>wrong account</Eyebrow>
          <Title className="mt-4 text-2xl">Nearly there — wrong Google account.</Title>
          <p className="mt-4 font-sans text-base text-ink/70">
            This invite went to {data?.participant?.email_masked || 'another account'}. Tap below to sign in with that one instead.
          </p>
          <div className="mt-8">
            <Button onClick={switchAccount}>Switch account</Button>
          </div>
        </>
      );
    case S.ERROR:
      return shell(<><Eyebrow>something went wrong</Eyebrow><Title className="mt-4 text-2xl">Couldn't load this.</Title><p className="mt-4 font-sans text-base text-ink/70">Try opening the link again.</p></>);
    default: // READY
      return shell(
        <>
          <Eyebrow>{who} proposed a time</Eyebrow>
          <Title className="mt-4 text-2xl">{formatSlot(slot)}</Title>
          {data?.proposal?.message && (
            <p className="mt-4 font-sans text-base text-ink/70">"{data.proposal.message}"</p>
          )}
          <p className="mt-4 font-mono text-xs text-ink/50">Accept and it lands in both your calendars.</p>
          <div className="mt-8 space-y-3">
            <Button onClick={() => respond('accept')} disabled={submitting}>
              {submitting ? 'One moment…' : 'Accept'}
            </Button>
            <Button variant="secondary" onClick={() => respond('reject')} disabled={submitting}>
              Decline
            </Button>
          </div>
        </>
      );
  }
}
